import { SEC_ICONS } from '../laws'
import { REACHABLE_NAV, REACHABLE_PINS, iconIndexOf } from '../reachable'

/**
 * The left rail: five pinned queues, then eighteen collapsible sections.
 *
 * Verbatim from `docs/design/reference/template.html` lines 85–118. 224px
 * open, 52px collapsed.
 *
 * A note on what nearly went wrong here the first time: a section header is
 * PLAIN TEXT with a toned SVG glyph. Putting `data-tone` on the header itself
 * paints a full-width tinted bar, because the tone attributes in tokens.css
 * are chip styling — background plus foreground. The design tints the 14px
 * icon and nothing else, and getting that wrong made the rail unrecognisable.
 *
 * # The rail renders no counts (#30)
 *
 * The design draws a badge on the five pins and on sixteen of the screen rows
 * (never on a section header), and those numbers came from `COUNTS` and
 * `PINS` in `../nav` — design
 * corpus that nobody read. The rail is chrome, not a screen, so the per-screen
 * provenance chip in `ScreenHeader` cannot answer for it: the console could
 * show "Live · postgres" over real figures and an invented "11" beside them,
 * and an operator who has learned that this console distinguishes the two
 * would reasonably read the rail as real.
 *
 * There is no `GET /v1/admin/queues` yet. Until there is, the badge slots stay
 * empty. **Wire them to that endpoint, never back to a constant** — one read
 * for the whole rail, in the shell, carrying the dashboard's
 * `{availability, value?, reason?, provenance?}` shape so a badge can say
 * `available` / `not_built` / `unavailable` instead of guessing.
 */

interface Props {
  readonly screen: string
  readonly collapsed: boolean
  /** Section label → explicitly opened/closed. Absent means the default. */
  readonly open: Readonly<Record<string, boolean>>
  readonly onToggleSection: (label: string) => void
  readonly onNavigate: (id: string) => void
  readonly onToggleCollapsed: () => void
}

export function NavRail({
  screen,
  collapsed,
  open,
  onToggleSection,
  onNavigate,
  onToggleCollapsed,
}: Props) {
  return (
    <nav
      aria-label="Console sections"
      style={{
        flex: '0 0 auto',
        width: collapsed ? '52px' : '224px',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '12px' }}>
        <div
          style={{
            padding: '8px 10px 4px',
            fontSize: '9.5px',
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--k-mut2)',
          }}
        >
          {collapsed ? 'Pin' : 'Pinned'}
        </div>

        {REACHABLE_PINS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="k-hov"
            onClick={() => onNavigate(id)}
            data-cur={screen === id ? '1' : ''}
            title={label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              width: '100%',
              height: '24px',
              padding: '0 10px',
              border: 0,
              background: 'transparent',
              color: 'var(--k-ink)',
              font: 'inherit',
              fontSize: '12px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                flex: '0 0 auto',
                width: '5px',
                height: '5px',
                background: 'var(--k-accent)',
              }}
            />
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {collapsed ? '' : label}
            </span>
            {/* The pin's badge slot, empty until `GET /v1/admin/queues` fills
                it. It renders anyway: it is a flex item, so dropping it also
                drops its 9px gap and lets the label run to the rail's edge,
                out of line with the section rows below. Empty and toneless —
                a `data-tone` on nothing would paint a tinted chip the moment
                the slot gained height. */}
            <span
              style={{
                padding: '0 4px',
                fontSize: '10px',
                fontWeight: 600,
                fontFamily: 'var(--k-mono)',
              }}
            />
          </button>
        ))}

        <div style={{ height: '2px', background: 'var(--k-rule)', margin: '8px 0' }} />

        {REACHABLE_NAV.map(([label, , items], si) => {
          const holdsScreen = items.some((i) => i[0] === screen)
          // Default: the section containing the current screen, plus the first
          // seven. Explicit toggles win.
          const isOpen = collapsed ? false : (open[label] ?? (holdsScreen || si < 7))
          // Indexed against the DESIGNED nav, never against the filtered one:
          // SEC_ICONS is positional, and hiding an unbuilt section shifts every
          // index after it. See iconIndexOf.
          const paths = SEC_ICONS[iconIndexOf(label)] ?? []

          return (
            <div key={label} style={{ marginBottom: '2px' }}>
              <button
                type="button"
                className="k-hov"
                onClick={() => onToggleSection(label)}
                aria-expanded={isOpen}
                title={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  height: '26px',
                  padding: '0 10px',
                  border: 0,
                  background: 'transparent',
                  color: 'var(--k-ink)',
                  font: 'inherit',
                  fontSize: '10.5px',
                  fontWeight: 800,
                  letterSpacing: '.07em',
                  textTransform: 'uppercase',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <svg
                  data-ic={holdsScreen ? 'r' : 'n'}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flex: '0 0 auto' }}
                  aria-hidden="true"
                >
                  {paths.map((d) => (
                    <path key={d} d={d} />
                  ))}
                </svg>
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {collapsed ? '' : label}
                </span>
                <span
                  style={{
                    color: 'var(--k-mut2)',
                    fontFamily: 'var(--k-mono)',
                    fontSize: '10px',
                    fontWeight: 400,
                  }}
                >
                  {collapsed ? '' : isOpen ? '–' : '+'}
                </span>
              </button>

              {isOpen && (
                <div style={{ paddingBottom: '4px' }}>
                  {items.map(([id, itemLabel]) => (
                      <button
                        key={id}
                        type="button"
                        className="k-hov"
                        onClick={() => onNavigate(id)}
                        data-cur={screen === id ? '1' : ''}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          minHeight: '23px',
                          padding: '2px 10px 2px 26px',
                          border: 0,
                          background: 'transparent',
                          color: 'var(--k-ink)',
                          font: 'inherit',
                          fontSize: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {itemLabel}
                        </span>
                        {/* Both trailing spans render unconditionally, empty
                            when they have nothing to say. They are flex items,
                            so omitting one also removes its 8px gap and slides
                            the badge sideways — the row would no longer line
                            up with its neighbours.
                            The count slot is empty on every row: the sixteen
                            numbers the design draws here were corpus, and
                            `GET /v1/admin/queues` does not exist yet (#30).
                            No `data-tone` either, for the reason in the header
                            comment — a tone on an empty slot is a tinted chip
                            waiting to appear. */}
                        <span
                          style={{
                            padding: '0 4px',
                            fontSize: '10px',
                            fontFamily: 'var(--k-mono)',
                            fontWeight: 600,
                          }}
                        />
                        {/* Phase 2 is marked; Phase 1 is unmarked, because
                            most of the console is Phase 1 and labelling the
                            majority tells nobody anything. */}
                        <span
                          style={{
                            color: 'var(--k-mut2)',
                            fontSize: '9.5px',
                            fontFamily: 'var(--k-mono)',
                          }}
                        >
                          {''}
                        </span>
                      </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className="k-hov"
        onClick={onToggleCollapsed}
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '28px',
          padding: '0 10px',
          border: 0,
          borderTop: '1px solid var(--k-line)',
          background: 'var(--k-panel)',
          color: 'var(--k-mut)',
          font: 'inherit',
          fontSize: '10.5px',
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {collapsed ? '›' : '‹ Collapse'}
      </button>
    </nav>
  )
}
